import React from 'react';
import { LocalServicePage } from '../../components/LocalServicePage';

export const LocksmithHornchurch = () => {
  return (
    <LocalServicePage
      areaName="Hornchurch"
      areaType="City"
      slug="locksmith-hornchurch"
      heroTitle="Door, Window & Lock Repairs in Hornchurch"
      heroSubtitle="Royal Security provides specialist repairs for doors, windows, locks and glazing throughout Hornchurch. We focus on accurate diagnosis and quality repairs—not unnecessary replacements."
      introContent={
        <>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Hornchurch is one of our core service areas, and we've worked on properties across the neighbourhood—from the family homes around Emerson Park to the streets near Hornchurch station and the quieter areas towards Upminster. This local knowledge helps us understand the typical issues that affect properties here.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Whether you're a homeowner dealing with a door that won't lock properly, a landlord needing repairs before a new tenancy, or a letting agent managing properties in the area, we provide the same standard of service: honest assessment, clear pricing, and repairs that last.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            Our approach is straightforward. We diagnose the actual problem, explain your options clearly, and carry out the repair using quality parts. We'd rather fix your existing door properly than sell you something you don't need.
          </p>
        </>
      }
      servicesEmphasis={[
        {
          title: "Multipoint Lock Repairs & Replacement",
          description: "Multipoint locks are standard on modern doors but can develop faults over time. We repair gearboxes, replace worn hooks and rollers, and fit new mechanisms when needed. Our diagnosis identifies exactly what's failed so you only pay for what needs replacing."
        },
        {
          title: "Composite Door Specialist",
          description: "Many Hornchurch properties have composite front doors. We handle all repairs—from stiff handles and misaligned locks to dropped hinges and damaged panels. We also upgrade hardware for improved security or convenience."
        },
        {
          title: "uPVC Window & Door Repairs",
          description: "uPVC doors and windows throughout Hornchurch benefit from our repair services. We fix handles, hinges, locks, and mechanisms. We replace worn seals and gaskets. If your uPVC is functioning poorly, we'll restore it."
        },
        {
          title: "Tilt & Turn Window Mechanisms",
          description: "Tilt and turn windows offer excellent ventilation but have complex mechanisms that can fail. We repair and replace the hardware—tilt restrictors, turn mechanisms, handles, and locking points—to restore full functionality."
        },
        {
          title: "Glazing Repairs & Upgrades",
          description: "Failed sealed units, cracked glass, and misted double glazing are common issues. We replace glass within your existing frames, with options to upgrade to better thermal or acoustic performance if desired."
        },
        {
          title: "Patio & French Door Repairs",
          description: "Sliding patio doors and French doors need smooth operation and secure locking. We service tracks and rollers, repair multipoint locks, adjust alignment, and replace worn components."
        }
      ]}
      localContextTitle="What to Tell Us When You Call in Hornchurch"
      localContext={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Describe the symptoms:</strong> What exactly is happening? Door won't lock at all? Locks but feels stiff? Handle floppy? Window stuck? The more detail you can give, the better we can prepare.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <strong>Send photos if possible:</strong> A picture of the door/window, the handle, the lock edge, or the problem area helps us identify the mechanism type and likely issue before we arrive.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            <strong>Note when it started:</strong> Sudden failure and gradual worsening can point to different causes, so tell us which it was. Recent changes (new carpet, building work, weather extremes) can also be relevant.
          </p>
        </>
      }
      commonProblems={[
        {
          title: "Door handle floppy or stiff",
          description: "Usually indicates a worn gearbox or spring cassette. Often repairable without replacing the entire mechanism."
        },
        {
          title: "Key turns but door won't lock",
          description: "The multipoint mechanism isn't engaging. Could be alignment, worn hooks, or gearbox failure."
        },
        {
          title: "Window won't stay open",
          description: "Friction stays or hinges are worn. Simple replacement restores proper function."
        },
        {
          title: "Draughts around door frame",
          description: "Weather seals and gaskets perish over time. Replacement seals are effective and economical."
        },
        {
          title: "Patio door sticking or jumping track",
          description: "Rollers and tracks need servicing or replacement. We restore smooth operation."
        },
        {
          title: "Letter box rattling or draughty",
          description: "Internal flaps and seals wear out. We fit quality replacements that keep out weather and noise."
        }
      ]}
      whyChooseUs={
        <>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            We're a short distance from Hornchurch and have been working in the area for years. This isn't just marketing—it means we can respond quickly, we understand the local housing stock, and we've built relationships with customers who come back to us and recommend us to neighbours.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Our commitment to repair-first means we save customers money without compromising on quality. A door that's been properly adjusted and had its mechanism serviced can give years more reliable service. We're not interested in selling you things you don't need.
          </p>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We guarantee our work and use quality parts. If something we've fitted fails prematurely, we put it right. That accountability matters, especially when you're trusting someone with your home security.
          </p>
        </>
      }
      ctaText="Need Door or Window Repairs in Hornchurch?"
    />
  );
};
